import { Role } from "../../core/models/entities/Role";

// Pure function: who is the user for Clarity (design section 4.2). Never
// an e-mail, never a person's name: the custom id is the B2C object id
// (hashed by Clarity in the browser), the friendly name is the company
// name for vendors ONLY (legal persons), "Kunde <org-id>" for end users
// (an end-user org can be a private person) and "Intern" for internal
// users (who are not recorded in prod at all - the facade stops before
// tags/identify).
export type ClarityRole = "forhandler" | "slutkunde" | "intern";

export interface ClarityIdentity {
  rolle: ClarityRole;
  forhandler: string;
  kunde: string;
  sprog: string;
  kmSession?: string;
  customId: string;
  friendlyName: string;
}

export interface ClarityIdentityInput {
  role: Role | undefined;
  organizationId: number | undefined;
  parentId?: number;
  organizationName?: string;
  b2CId?: string;
  msalLocalAccountId?: string;
  // Every e-mail candidate from the MSAL account (collectEmails); read only
  // to a boolean here and never part of the output.
  emails: string[];
  internalDomains: string[];
  actAsActive: boolean;
  actAsOrgId?: number;
  language: string;
  kmSession?: string;
}

export function isInternalEmail(
  emails: string[],
  internalDomains: string[],
): boolean {
  return emails.some((e) => {
    const lower = e.trim().toLowerCase();
    return internalDomains.some((d) => d && lower.endsWith("@" + d));
  });
}

export function buildClarityIdentity(
  input: ClarityIdentityInput,
): ClarityIdentity | null {
  const { role, organizationId } = input;
  if (organizationId == null) return null;
  const customId = input.msalLocalAccountId || input.b2CId;
  if (!customId) return null;

  const internal =
    input.actAsActive ||
    role === Role.Admin ||
    isInternalEmail(input.emails, input.internalDomains);
  const rolle: ClarityRole = internal
    ? "intern"
    : role === Role.Vendor
      ? "forhandler"
      : "slutkunde";

  const vendorOrgId =
    role === Role.Vendor
      ? organizationId
      : role === Role.EndUser
        ? (input.parentId ?? organizationId)
        : input.actAsActive && input.actAsOrgId != null
          ? input.actAsOrgId
          : 0;

  const companyName = input.organizationName?.trim();
  const friendlyName = internal
    ? "Intern"
    : rolle === "forhandler" && companyName
      ? companyName
      : "Kunde " + String(organizationId);

  return {
    rolle,
    forhandler: String(vendorOrgId),
    kunde: String(organizationId),
    sprog: (input.language || "en").split("-")[0],
    kmSession: input.kmSession,
    customId,
    friendlyName,
  };
}
